<template>
  <section class="home-feed">
    <div class="home-feed__inner">
      <div class="home-feed__head">
        <h2 class="home-feed__title">Latest Products</h2>
      </div>

      <div v-if="products.length" class="home-feed__list">
        <HomeProductCard
          v-for="product in products"
          :key="product.id"
          :product="product"
        />
      </div>

      <p v-else-if="!loading" class="home-feed__empty">No products published yet.</p>

      <div ref="sentinel" class="home-feed__sentinel" aria-hidden="true" />

      <p v-if="loading" class="home-feed__status">Loading more products…</p>
      <p v-else-if="error" class="home-feed__status home-feed__status--error">
        {{ error }}
        <button type="button" class="home-feed__retry" @click="loadMore">Try again</button>
      </p>
      <p v-else-if="!hasMore && products.length" class="home-feed__status">You're all caught up.</p>
    </div>
  </section>
</template>

<script>
import { defineComponent } from 'vue';
import axios from 'axios';
import HomeProductCard from './HomeProductCard.vue';

export default defineComponent({
  name: 'HomeProductFeed',
  components: {
    HomeProductCard
  },
  props: {
    blockData: {
      type: Object,
      required: true,
      default: () => ({
        products: [],
        page: 1,
        per_page: 5,
        has_more: false,
        api_url: ''
      })
    }
  },
  data() {
    return {
      products: [...(this.blockData.products || [])],
      page: this.blockData.page || 1,
      perPage: this.blockData.per_page || 5,
      hasMore: this.blockData.has_more ?? false,
      apiUrl: this.blockData.api_url || '',
      loading: false,
      error: '',
      observer: null
    };
  },
  mounted() {
    if (!this.hasMore || typeof IntersectionObserver === 'undefined') {
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadMore();
        }
      },
      { root: null, rootMargin: '200px 0px', threshold: 0 }
    );

    if (this.$refs.sentinel) {
      this.observer.observe(this.$refs.sentinel);
    }
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    async loadMore() {
      if (this.loading || !this.hasMore || !this.apiUrl) {
        return;
      }

      this.loading = true;
      this.error = '';

      try {
        const { data } = await axios.get(this.apiUrl, {
          params: {
            page: this.page + 1,
            per_page: this.perPage
          }
        });

        const incoming = data.products || [];
        const existingIds = new Set(this.products.map((product) => product.id));
        const unique = incoming.filter((product) => !existingIds.has(product.id));

        this.products = [...this.products, ...unique];
        this.page = data.page;
        this.hasMore = data.has_more;

        if (!this.hasMore && this.observer) {
          this.observer.disconnect();
        }
      } catch (err) {
        this.error = 'Could not load more products.';
        console.error(err);
      } finally {
        this.loading = false;
      }
    }
  }
});
</script>
